/**
 * Loads the signed-in person's console choices before the console draws, and keeps them —
 * and, alongside, which areas of the console are theirs, so the bar is drawn once and right.
 *
 * Before the console is drawn, because the pieces that read a choice read it once, at
 * mount — the pinned view, the desk's width, the map's legend — and a preference that
 * arrived a moment later would be a preference nobody saw until the next reload. The wait
 * is one small request; a request that fails leaves the browser's own copy in charge, so
 * a console with the API down still opens.
 *
 * Applying means writing the choice where each piece already looks: the theme module, the
 * language, the storage keys. See `preferences.ts` for why the browser copy is kept.
 */

import { ApiError } from '@taxi/api-client';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { setLanguage } from '../i18n';
import { choose as chooseTheme, preference as themePreference } from '../theme';
import {
  DESK_ONLY,
  NOTHING_CHOSEN,
  preferencesApi,
  type ConsoleAccess,
  type ConsolePreferences,
} from './preferences';
import { NotStaff } from './NotStaff';
import { PreferencesContext, type Preferences } from './preferencesContext';
import { defaultView, rememberDefault } from './views';

/** The two keys the desk and the map read at mount, written here so they find the person's choice. */
const DESK_WIDTH_KEY = 'taxi.console.deskWidth';
const SHOW_ON_BREAK_KEY = 'taxi.console.map.showOnBreak';

function applyToBrowser(mine: ConsolePreferences): void {
  if (mine.theme !== null && mine.theme !== themePreference()) chooseTheme(mine.theme);
  if (mine.language !== null) void setLanguage(mine.language);
  if (mine.defaultView !== null) rememberDefault(globalThis.localStorage, mine.defaultView);

  try {
    if (mine.deskWidth !== null) globalThis.localStorage?.setItem(DESK_WIDTH_KEY, String(mine.deskWidth));
    if (mine.showOnBreak !== null) {
      if (mine.showOnBreak) globalThis.localStorage?.removeItem(SHOW_ON_BREAK_KEY);
      else globalThis.localStorage?.setItem(SHOW_ON_BREAK_KEY, 'no');
    }
  } catch {
    // Storage refused: the pieces fall back to their defaults, as they would anyway.
  }
}

/**
 * What the browser holds now, for a person who never chose: their first save must not
 * withdraw a theme or a view the PC was already showing them.
 */
function fromBrowser(): ConsolePreferences {
  return {
    ...NOTHING_CHOSEN,
    theme: themePreference(),
    defaultView: defaultView(globalThis.localStorage),
  };
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<ConsolePreferences | null>(null);
  const [access, setAccess] = useState<ConsoleAccess | null>(null);
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void preferencesApi
      .mine()
      .then((mine) => {
        if (cancelled) return;

        applyToBrowser(mine);
        setCurrent({ ...fromBrowser(), ...withoutNulls(mine) });
      })
      .catch(() => {
        if (!cancelled) setCurrent(fromBrowser());
      });

    // In parallel, not after: the two are one wait. Failing closed — a console that could
    // not ask offers the desk and nothing else, and the desk is what an outage needs.
    //
    // Except a refusal, which is not an outage but an answer: the endpoint is the office's,
    // so a 403 says this person is not office staff at this firm — a driver who accepted an
    // invitation, most likely. The desk would be a screen of refused lists; they are told
    // where they do work instead.
    void preferencesApi
      .access()
      .then((mine) => {
        if (!cancelled) setAccess(mine);
      })
      .catch((failure: unknown) => {
        if (cancelled) return;

        if (failure instanceof ApiError && failure.status === 403) setRefused(true);
        else setAccess(DESK_ONLY);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback((patch: Partial<ConsolePreferences>) => {
    setCurrent((was) => {
      const next = { ...(was ?? NOTHING_CHOSEN), ...patch };

      // Sent from a microtask rather than from inside the updater, which React may run
      // twice in development. Fire and forget: the choice is already applied on this
      // screen, and a save that fails costs one re-choice at the next desk, which is what
      // the person had before.
      queueMicrotask(() => void preferencesApi.save(next).catch(() => undefined));

      return next;
    });
  }, []);

  const value = useMemo<Preferences | null>(
    () => (current === null || access === null ? null : { current, access, save }),
    [current, access, save],
  );

  if (refused) return <NotStaff />;
  if (value === null) return null;

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

function withoutNulls(mine: ConsolePreferences): Partial<ConsolePreferences> {
  return Object.fromEntries(
    Object.entries(mine).filter(([, value]) => value !== null),
  ) as Partial<ConsolePreferences>;
}
